import React, { useState, useEffect } from "react";
import {
  BarChart3,
  FileEdit,
  UploadCloud,
  BellRing,
  Activity,
  Package,
  Users,
  BookOpen,
  Sheet,
  Cloud,
  Database,
  FileText,
  Zap,
  ChevronDown,
  ArrowRight,
  Wrench,
  Droplets,
  Scissors,
  Calendar,
  CalendarDays,
  CalendarRange,
  ClipboardCheck
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useModuleAccess from "../hooks/useModuleAccess";
import { MODULE_ICONS } from './icons/ModuleIcons';

// Sheet names for Generation Reports submenu
const generationReportSheets = [
  { name: "Daily Report", icon: <Sheet size={14} />, formType: "Daily Report" },
  { name: "Weather", icon: <Cloud size={14} />, formType: "Weather" },
  { name: "Inverters", icon: <Database size={14} />, formType: "Inverters" },
  { name: "Breakdowns", icon: <FileText size={14} />, formType: "Breakdowns" },
  { name: "Plant Meters", icon: <Zap size={14} />, formType: "Plant Meters" }
];

// Sheet names for O&M submenu
const omSheets = [
  { name: "Module Cleaning", icon: <Droplets size={14} />, formType: "Module Cleaning", color: "blue" },
  { name: "Grass Cutting", icon: <Scissors size={14} />, formType: "Grass Cutting", color: "green" },
  { name: "Daily Checklist", icon: <Calendar size={14} />, formType: "Daily Checklist", color: "amber" },
  { name: "Weekly Checklist", icon: <CalendarDays size={14} />, formType: "Weekly Checklist", color: "emerald" },
  { name: "Monthly Checklist", icon: <CalendarRange size={14} />, formType: "Monthly Checklist", color: "violet" },
  { name: "Quarterly Checklist", icon: <ClipboardCheck size={14} />, formType: "Quarterly Checklist", color: "orange" },
  { name: "Yearly Checklist", icon: <ClipboardCheck size={14} />, formType: "Yearly Checklist", color: "red" }
];

// In the AppLauncher dropdown, items have a light background, so icons and text
// stay dark brand blue on hover rather than inverting to white.
const apps = [
  { id: 'analytics', moduleKey: 'dashboard', label: 'Performance Analytics', icon: MODULE_ICONS.dashboard, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/dashboard/{site}` },
  { id: 'reports', moduleKey: 'generation_reports', label: 'Generation Reports', icon: MODULE_ICONS.generation_reports, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/entry/{site}` },
  { id: 'om', moduleKey: 'om', label: 'O&M', icon: MODULE_ICONS.om, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/om/{site}` },
  { id: 'upload', moduleKey: 'upload', label: 'Upload Plant Data', icon: MODULE_ICONS.upload, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/upload/{site}` },
  { id: 'alerts', moduleKey: 'alerts', label: 'Alerts Portal', icon: MODULE_ICONS.alerts, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/alerts/{site}` },
  { id: 'simulation', moduleKey: 'simulation', label: 'Simulation-On-Demand', icon: MODULE_ICONS.simulation, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/simulation/{site}` },
  { id: 'dsm', moduleKey: 'dsm', label: 'DSM', icon: MODULE_ICONS.dsm, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/dsm/{site}` },
  { id: 'inventory', moduleKey: 'inventory', label: 'Inventory Management', icon: MODULE_ICONS.inventory, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/inventory/{site}` },
  { id: 'vendors', moduleKey: 'vendors', label: 'Vendor Management', icon: MODULE_ICONS.vendors, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/vendors/{site}` },
  { id: 'documentation', moduleKey: 'documentation', label: 'Documentation', icon: MODULE_ICONS.documentation, color: 'text-[#0084CF] group-hover:text-[#005282]', bgColor: 'bg-[#F2F8FC]', hoverBg: 'group-hover:bg-[#D9ECF7]', route: `/documentation/{site}` },
];

interface AppLauncherProps {
  site?: string;
}

const AppLauncher: React.FC<AppLauncherProps> = ({ site }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showGenerationReportsSubmenu, setShowGenerationReportsSubmenu] = useState(false);
  const [showOMSubmenu, setShowOMSubmenu] = useState(false);
  const navigate = useNavigate();
  const normalizedSite = String(site || '').trim().toUpperCase();
  const hasSiteContext = !!normalizedSite;
  const { hasModuleAccess } = useModuleAccess(hasSiteContext ? normalizedSite : undefined);

  const roleName = String(sessionStorage.getItem('role_name') || sessionStorage.getItem('user_role') || '').trim().toLowerCase().replace(/ /g, '_');
  const isSystemAdmin = roleName === 'system_admin';
  const isHoAdmin = roleName === 'ho_admin' || roleName === 'develop_admin';

  // Close submenus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.app-launcher-container') && !target.closest('.submenu-popup')) {
        setIsOpen(false);
        setShowGenerationReportsSubmenu(false);
        setShowOMSubmenu(false);
      }
    };

    if (isOpen || showGenerationReportsSubmenu || showOMSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, showGenerationReportsSubmenu, showOMSubmenu]);

  // Replace {site} placeholder in routes
  const appsWithRoutes = apps
    .filter((app) => hasModuleAccess(app.moduleKey))
    .map(app => ({
      ...app,
      route: app.route.replace('{site}', normalizedSite)
    }));

  return (
    <div className="relative app-launcher-container">
      {/* 9-Dots Grid Icon */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center group"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="App Launcher"
      >
        <div className="grid grid-cols-3 gap-1 w-5 h-5 items-center justify-items-center">
          {[...Array(9)].map((_, i) => (
            <div
              key={i}
              className={`w-[5px] h-[5px] rounded-full flex-shrink-0 transition-colors ${isOpen ? 'bg-[#0084CF]' : 'bg-gray-500 group-hover:bg-gray-900'}`}
            />
          ))}
        </div>
      </motion.button>

      {/* Popup Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for closing */}
            <div
              className="fixed inset-0 z-[-1]"
              style={{ width: '100vw', height: '100vh', top: '-64px', left: '-10px' }}
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute left-0 top-12 w-[415px] glass-dropdown rounded-lg shadow-xl border border-white/20 overflow-hidden z-[60]"
            >
              {/* Header */}
              <div className="px-4 py-2.5 flex items-center justify-between border-b border-gray-200/40 bg-transparent">
                <span className="text-[11px] app-launcher-text-light text-black-900 uppercase tracking-wider">AxonMetric Hub</span>
              </div>

              {/* Apps Grid */}
              <div className="px-2 py-1.5 grid grid-cols-2 gap-1">
                {appsWithRoutes.map((app) => (
                  <div key={app.id} className="relative">
                    <button
                      onClick={() => {
                        if (!hasSiteContext) {
                          navigate('/select-site');
                          setIsOpen(false);
                          return;
                        }

                        if (app.id === 'reports') {
                          setShowGenerationReportsSubmenu(!showGenerationReportsSubmenu);
                        } else if (app.id === 'om') {
                          setShowOMSubmenu(!showOMSubmenu);
                        } else {
                          navigate(app.route);
                          setIsOpen(false);
                        }
                      }}
                      className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-[#EBF5FB] transition-colors text-left group"
                    >
                      <div className={`w-6 h-6 flex items-center justify-center rounded-md overflow-hidden shrink-0 border border-[#E1EFF8] group-hover:border-[#BCE0F5] ${app.bgColor} ${app.hoverBg} transition-colors`}>
                        <app.icon size={14} className={app.color} />
                      </div>
                      <span className="text-[10px] app-launcher-text-light text-gray-900 group-hover:text-[#005282] leading-[1.1] flex-1">
                        {app.label}
                      </span>
                      {app.id === 'reports' && (
                        <motion.div
                          animate={{ rotate: showGenerationReportsSubmenu ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-[#0084CF] group-hover:text-[#005282] shrink-0"
                        >
                          <ChevronDown size={10} />
                        </motion.div>
                      )}
                      {app.id === 'om' && (
                        <motion.div
                          animate={{ rotate: showOMSubmenu ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-[#0084CF] group-hover:text-[#005282] shrink-0"
                        >
                          <ChevronDown size={10} />
                        </motion.div>
                      )}
                    </button>

                    {/* Generation Reports Submenu */}
                    {app.id === 'reports' && showGenerationReportsSubmenu && (
                      <AnimatePresence>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 right-0 mt-1 glass-dropdown rounded-lg shadow-xl border border-white/20 overflow-hidden z-50 submenu-popup"
                          style={{ minWidth: '190px' }}
                        >
                          <div className="p-1">
                            <div className="text-[9px] app-launcher-text-light text-gray-800 uppercase tracking-wider mb-1 px-1.5">Select Sheet</div>
                            {generationReportSheets.map((sheet, sheetIndex) => (
                              <button
                                key={sheetIndex}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!hasSiteContext) {
                                    navigate('/select-site');
                                    setIsOpen(false);
                                    return;
                                  }

                                  navigate(`/entry/${normalizedSite}`);
                                  // Store the selected form type in localStorage
                                  localStorage.setItem(`axon_active_form_${normalizedSite}`, sheet.formType);
                                  setIsOpen(false);
                                  setShowGenerationReportsSubmenu(false);
                                }}
                                className="w-full flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-[#EBF5FB] transition-colors text-left group"
                              >
                                <div className="text-[#0084CF] group-hover:text-[#1C4D88] shrink-0">
                                  {sheet.icon}
                                </div>
                                <span className="text-[9px] app-launcher-text-light text-gray-900 group-hover:text-[#1C4D88] leading-[1.1]">
                                  {sheet.name}
                                </span>
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    )}

                    {/* O&M Submenu */}
                    {app.id === 'om' && showOMSubmenu && (
                      <AnimatePresence>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 right-0 mt-1 glass-dropdown rounded-lg shadow-xl border border-white/20 overflow-hidden z-50 submenu-popup"
                          style={{ minWidth: '190px' }}
                        >
                          <div className="p-1">
                            <div className="text-[9px] app-launcher-text-light text-gray-800 uppercase tracking-wider mb-1 px-1.5">Select Checklist</div>
                            {omSheets.map((sheet, sheetIndex) => {
                              // Define color classes for each sheet
                              // Checklist groups keep their identity through brand
                              // intensity rather than separate hues; hover matches
                              // the rest of the module surfaces.
                              const colorClasses = {
                                blue:    { icon: 'text-[#0066A3]', iconHover: 'group-hover:text-[#1C4D88]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' },
                                green:   { icon: 'text-[#0084CF]', iconHover: 'group-hover:text-[#1C4D88]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' },
                                amber:   { icon: 'text-[#3EA3DA]', iconHover: 'group-hover:text-[#1C4D88]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' },
                                emerald: { icon: 'text-[#0084CF]', iconHover: 'group-hover:text-[#1C4D88]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' },
                                violet:  { icon: 'text-[#0066A3]', iconHover: 'group-hover:text-[#1C4D88]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' },
                                orange:  { icon: 'text-[#8AC4E6]', iconHover: 'group-hover:text-[#1C4D88]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' },
                                red:     { icon: 'text-[#1C4D88]', iconHover: 'group-hover:text-[#0D2747]', bg: 'hover:bg-[#EBF5FB]', textHover: 'group-hover:text-[#1C4D88]' }
                              };
                              const colors = colorClasses[sheet.color as keyof typeof colorClasses];

                              return (
                                <button
                                  key={sheetIndex}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!hasSiteContext) {
                                      navigate('/select-site');
                                      setIsOpen(false);
                                      return;
                                    }

                                    navigate(`/om/${normalizedSite}?form=${sheet.formType}`);
                                    setIsOpen(false);
                                    setShowOMSubmenu(false);
                                  }}
                                  className={`w-full flex items-center gap-1 px-1.5 py-1 rounded-md ${colors.bg} transition-colors text-left group`}
                                >
                                  <div className={`${colors.icon} ${colors.iconHover} shrink-0`}>
                                    {sheet.icon}
                                  </div>
                                  <span className={`text-[9px] app-launcher-text-light text-gray-900 ${colors.textHover} leading-[1.1]`}>
                                    {sheet.name}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-3 py-3 bg-transparent border-t border-gray-200/40 flex items-center gap-1.5">
                <button
                  onClick={() => { setIsOpen(false); navigate(`/navigate/${site}`); }}
                  className="app-launcher-footer-btn flex items-center gap-1 text-[10.5px] font-bold text-gray-700 bg-gray-50 hover:bg-gray-100 hover:text-[#0084CF] border border-gray-200 px-2 py-1 rounded-md transition-colors group whitespace-nowrap"
                >
                  <span className="app-launcher-footer-btn-text">Navigation</span> <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                
                {(isHoAdmin || isSystemAdmin) && (
                  <button
                    onClick={() => { setIsOpen(false); navigate('/select-site'); }}
                    className="app-launcher-footer-btn flex items-center gap-1 text-[10.5px] font-bold text-gray-700 bg-gray-50 hover:bg-gray-100 hover:text-[#0084CF] border border-gray-200 px-2 py-1 rounded-md transition-colors group whitespace-nowrap"
                  >
                    <span className="app-launcher-footer-btn-text">Site Selection</span> <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                )}

                {isSystemAdmin && (
                  <button
                    onClick={() => { setIsOpen(false); navigate('/select-company'); }}
                    className="app-launcher-footer-btn flex items-center gap-1 text-[10.5px] font-bold text-gray-700 bg-gray-50 hover:bg-gray-100 hover:text-[#0084CF] border border-gray-200 px-2 py-1 rounded-md transition-colors group whitespace-nowrap"
                  >
                    <span className="app-launcher-footer-btn-text">Company Selection</span> <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppLauncher;
